import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    CompareResult,
    GroupHeadcountResult,
    HeadcountResult,
    HrGroupBy,
    HrView,
    RetentionResult,
    TurnoverResult,
} from "models/hr-dashboard";
import FiscalCalendar from "utils/fiscal-calendar";

// Data for each tab. Each tab is loaded the first time it is opened, and again when the
// fiscal year (or the department / division toggle) changes.
export interface HrDashboardViews {
    "headcount"?: HeadcountResult | null;
    "by-group"?: GroupHeadcountResult | null;
    "consultants"?: HeadcountResult | null;
    "compare"?: CompareResult | null;
    "movement"?: HeadcountResult | null;
    "turnover"?: TurnoverResult | null;
    "retention"?: RetentionResult | null;
}

export interface HrDashboardSliceState {
    fiscalYear: number;
    groupBy: HrGroupBy;
    views: HrDashboardViews;
    loadedFor: Partial<Record<HrView, string>>; // the fy|groupBy each view was loaded for
    isLoading: boolean;
}

const initialState: HrDashboardSliceState = {
    fiscalYear: FiscalCalendar.lastMonthFiscalYear(),
    groupBy: "department",
    views: {},
    loadedFor: {},
    isLoading: false,
};

const HrDashboardSlice = createSlice({
    name: "HrDashboard",
    initialState,
    reducers: {
        setHrFiscalYear(state, action: PayloadAction<number>) {
            state.fiscalYear = action.payload;
        },
        setHrGroupBy(state, action: PayloadAction<HrGroupBy>) {
            state.groupBy = action.payload;
        },
        setHrView(state, action: PayloadAction<{ view: HrView; key: string; data: any }>) {
            (state.views as any)[action.payload.view] = action.payload.data;
            state.loadedFor[action.payload.view] = action.payload.key;
        },
        // Forget loaded data (e.g. after the settings change)
        resetHrViews(state) {
            state.views = {};
            state.loadedFor = {};
        },
        setHrDashboardLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
    },
});

export const {
    setHrFiscalYear,
    setHrGroupBy,
    setHrView,
    resetHrViews,
    setHrDashboardLoading,
} = HrDashboardSlice.actions;

export default HrDashboardSlice.reducer;
